import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { useState } from 'react'

import type { AnalyticsErrorRow, AnalyticsErrors, AnalyticsFunnel, AnalyticsPages, AnalyticsSectionRow, VitalRating } from '@/types/analytics'

const titleClass = 'w-fit text-sm font-semibold underline decoration-faint decoration-dotted underline-offset-4'

const PAGE_TYPE_LABELS: Record<string, string> = {
  home: 'Home',
  product: 'Product pages',
  category: 'Category and shop',
  search: 'Search',
  cart: 'Cart',
  checkout: 'Checkout',
  checkout_form: 'Filled form',
  order_success: 'Order confirmation',
  blog: 'Blog',
  campaign: 'Campaigns',
  account: 'Account',
  other: 'Other pages',
}

const pageLabel = (type: string) => PAGE_TYPE_LABELS[type] ?? type
const sectionLabel = (type: string) => type.replace(/[-_]/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

type QueryState<T> = { data?: T; isLoading: boolean; isError: boolean; error: Error | null }

const State = <T,>({ q, empty, emptyText, children }: { q: QueryState<T>; empty: boolean; emptyText: string; children: (data: T) => React.ReactNode }) =>
  q.isLoading ? (
    <Skeleton className="h-40" />
  ) : q.isError ? (
    <p className="text-sm text-error">{q.error?.message}</p>
  ) : !q.data || empty ? (
    <p className="py-6 text-center text-sm text-muted-foreground">{emptyText}</p>
  ) : (
    <>{children(q.data)}</>
  )

const RATING: Record<Exclude<VitalRating, null>, { label: string; tone: 'success' | 'warning' | 'error' }> = {
  good: { label: 'Good', tone: 'success' },
  needs_improvement: { label: 'Needs work', tone: 'warning' },
  poor: { label: 'Poor', tone: 'error' },
}

const Vital = ({ value, rating }: { value: string | null; rating: VitalRating }) =>
  value === null ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className="inline-flex items-center justify-end gap-1.5">
      <span className="tabular-nums">{value}</span>
      {rating && <Badge tone={RATING[rating].tone}>{RATING[rating].label}</Badge>}
    </span>
  )

const seconds = (ms: number | null) => (ms === null ? null : `${(ms / 1000).toFixed(1)} s`)

/** Page speed per page type: Web Vitals (75th percentile, as Google judges them) from real visits. */
export const SpeedCard = ({ q }: { q: QueryState<AnalyticsPages> }) => (
  <Card>
    <CardHeader>
      <CardTitle className={titleClass}>Page speed</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Measured on your shoppers' own devices when they land on a page. Most shoppers get this speed or better. Slow product and checkout pages usually cost the
        most orders.
      </p>
      <State q={q} empty={!q.data?.pages.some((p) => p.vitals_samples > 0)} emptyText="No speed measurements in these dates yet.">
        {(d) => (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Page</TableHead>
                <TableHead className="text-right">Views</TableHead>
                <TableHead className="text-right">Loading (LCP)</TableHead>
                <TableHead className="text-right">Response to taps (INP)</TableHead>
                <TableHead className="text-right">Layout shift (CLS)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.pages
                .filter((p) => p.vitals_samples > 0)
                .map((p) => (
                  <TableRow key={p.page_type}>
                    <TableCell>
                      {pageLabel(p.page_type)}
                      {p.low_sample && (
                        <Badge tone="soft" className="ml-1.5">
                          Few visits
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{p.views.toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <Vital value={seconds(p.lcp_ms)} rating={p.rating.lcp} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Vital value={p.inp_ms === null ? null : `${p.inp_ms} ms`} rating={p.rating.inp} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Vital value={p.cls === null ? null : p.cls.toFixed(2)} rating={p.rating.cls} />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </State>
    </CardContent>
  </Card>
)

const DEVICE_LABELS: Record<string, string> = { mobile: 'Mobile', desktop: 'Desktop', tablet: 'Tablet', unknown: 'Unknown' }
const STEP_SHORT: Record<string, string> = {
  product_view: 'Viewed product',
  add_to_cart: 'Added to cart',
  checkout: 'Checkout',
  checkout_form: 'Filled form',
  payment: 'Placed order',
  purchase: 'Confirmed',
}

/** The funnel per device, side by side: where mobile shoppers drop off compared with desktop. */
export const DeviceFunnelCard = ({ q }: { q: QueryState<AnalyticsFunnel> }) => (
  <Card>
    <CardHeader>
      <CardTitle className={titleClass}>Mobile vs desktop</CardTitle>
    </CardHeader>
    <CardContent>
      <State q={q} empty={!q.data?.groups.length} emptyText="No visits in these dates.">
        {(d) => (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Device</TableHead>
                <TableHead className="text-right">Sessions</TableHead>
                {d.groups[0]?.steps.slice(1).map((s) => (
                  <TableHead key={s.step} className="text-right">
                    {STEP_SHORT[s.step] ?? s.step}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.groups.map((g) => (
                <TableRow key={g.value}>
                  <TableCell>
                    {DEVICE_LABELS[g.value] ?? g.value}
                    {g.low_sample && (
                      <Badge tone="soft" className="ml-1.5">
                        Few visits
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{g.sessions.toLocaleString()}</TableCell>
                  {g.steps.slice(1).map((s) => (
                    <TableCell key={s.step} className="text-right tabular-nums">
                      {s.rate_from_start}%
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </State>
      <p className="mt-3 text-xs text-muted-foreground">Share of each device's sessions that reached the step.</p>
    </CardContent>
  </Card>
)

/** Sections shoppers actually scrolled to, per page, and how those visits went on. */
export const SectionsCard = ({ q }: { q: QueryState<AnalyticsSectionRow[]> }) => {
  const pages = [...new Set((q.data ?? []).map((r) => r.page_type))]
  const [page, setPage] = useState<string | null>(null)
  const current = page && pages.includes(page) ? page : (pages.includes('home') ? 'home' : pages[0])
  const rows = (q.data ?? []).filter((r) => r.page_type === current)

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className={titleClass}>Sections shoppers see</CardTitle>
        {pages.length > 1 && (
          <Tabs value={current} onValueChange={setPage}>
            <TabsList>
              {pages.slice(0, 5).map((p) => (
                <TabsTrigger key={p} value={p}>
                  {pageLabel(p)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          How far down the page shoppers get, and how visits that saw each section went on. Shoppers who scroll further are often more interested to begin with, so a
          higher rate is a lead to test, not proof the section works.
        </p>
        <State q={q} empty={!rows.length} emptyText="No section views in these dates yet.">
          {() => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Section</TableHead>
                  <TableHead className="text-right">Seen by</TableHead>
                  <TableHead className="text-right">Added to cart</TableHead>
                  <TableHead className="text-right">Ordered</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.position}-${r.section_type}-${r.variant}`}>
                    <TableCell>
                      <span className="tabular-nums text-muted-foreground">{r.position + 1}.</span> {sectionLabel(r.section_type)}
                      <span className="ml-1 text-xs text-muted-foreground">{r.variant}</span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.reach}%</TableCell>
                    <TableCell className="text-right tabular-nums">{r.add_to_cart_rate}%</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.conversion_rate}%
                      {r.low_sample && (
                        <Badge tone="soft" className="ml-1.5">
                          Few visits
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </State>
      </CardContent>
    </Card>
  )
}

type ErrorView = 'scripts' | 'api' | 'orders'

const errorLabel = (view: ErrorView, r: AnalyticsErrorRow) =>
  view === 'scripts'
    ? `${r.message || 'Unknown error'}${r.source ? ` · ${r.source}` : ''}`
    : view === 'api'
      ? `${r.endpoint ?? 'unknown'} · ${r.status ?? ''}`
      : `${r.method ?? 'unknown method'} · ${(r.reason ?? 'unknown').replace(/_/g, ' ')}`

/** Technical problems shoppers ran into, by how many visits they affected. */
export const ErrorsCard = ({ q }: { q: QueryState<AnalyticsErrors> }) => {
  const [view, setView] = useState<ErrorView>('scripts')
  const rows = q.data?.[view] ?? []
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className={titleClass}>Problems shoppers hit</CardTitle>
        <Tabs value={view} onValueChange={(v) => setView(v as ErrorView)}>
          <TabsList>
            <TabsTrigger value="scripts">Page errors{q.data?.scripts.length ? ` (${q.data.scripts.length})` : ''}</TabsTrigger>
            <TabsTrigger value="api">Failed requests{q.data?.api.length ? ` (${q.data.api.length})` : ''}</TabsTrigger>
            <TabsTrigger value="orders">Failed orders{q.data?.orders.length ? ` (${q.data.orders.length})` : ''}</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent>
        <State q={q} empty={!rows.length} emptyText="Nothing went wrong for shoppers in these dates.">
          {() => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Problem</TableHead>
                  <TableHead>Pages</TableHead>
                  <TableHead className="text-right">Visits affected</TableHead>
                  <TableHead className="text-right">Times</TableHead>
                  <TableHead className="text-right">Last seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="max-w-96 truncate font-mono text-xs" title={errorLabel(view, r)}>
                      {errorLabel(view, r)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.page_types.filter(Boolean).map(pageLabel).join(', ')}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.sessions.toLocaleString()}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.count.toLocaleString()}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{new Date(r.last_seen).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </State>
      </CardContent>
    </Card>
  )
}

const FILTER_LABELS: Record<string, string> = { category_id: 'Category', brand_id: 'Brand', min_price: 'Minimum price', max_price: 'Maximum price' }
const SORT_LABELS: Record<string, string> = {
  newest_first: 'Newest',
  oldest_first: 'Oldest',
  price_asc: 'Price: low to high',
  price_desc: 'Price: high to low',
  name_asc: 'Name: A to Z',
  name_desc: 'Name: Z to A',
  discount_desc: 'Biggest discount',
}

/** How shoppers look for products: searches, which filters and which sort orders they use. */
export const BrowsingCard = ({ q }: { q: QueryState<AnalyticsPages> }) => (
  <Card>
    <CardHeader>
      <CardTitle className={titleClass}>Search and filters</CardTitle>
    </CardHeader>
    <CardContent>
      <State
        q={q}
        empty={!q.data || (!q.data.searches.count && !q.data.filters.length && !q.data.sorts.length && !q.data.categories.length && !q.data.coupons.valid && !q.data.coupons.invalid)}
        emptyText="No searches, filters or category visits in these dates."
      >
        {(d) => (
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <div className="rounded-lg bg-subdued p-3">
                <p className="text-xs text-muted-foreground">Searches</p>
                <p className="mt-1 text-lg font-semibold tabular-nums text-ink">{d.searches.count.toLocaleString()}</p>
                <p className="text-xs tabular-nums text-muted-foreground">in {d.searches.sessions.toLocaleString()} sessions</p>
              </div>
              {d.coupons.valid + d.coupons.invalid > 0 && (
                <div className="rounded-lg bg-subdued p-3">
                  <p className="text-xs text-muted-foreground">Coupon codes tried</p>
                  <p className="mt-1 text-lg font-semibold tabular-nums text-ink">{(d.coupons.valid + d.coupons.invalid).toLocaleString()}</p>
                  <p className="text-xs tabular-nums text-muted-foreground">{d.coupons.invalid.toLocaleString()} did not work</p>
                </div>
              )}
            </div>
            {[
              { title: 'Categories visited', rows: d.categories.map((c) => ({ value: c.name ?? 'Deleted category', count: c.sessions })), labels: {} as Record<string, string> },
              { title: 'Filters and sorting', rows: [...d.filters.map((f) => ({ ...f, value: FILTER_LABELS[f.value] ?? f.value })), ...d.sorts.map((f) => ({ ...f, value: `Sort: ${SORT_LABELS[f.value] ?? f.value}` }))], labels: {} as Record<string, string> },
            ].map((b) => (
              <div key={b.title}>
                <p className="mb-1.5 text-xs text-muted-foreground">{b.title}</p>
                {b.rows.length ? (
                  <ul className="space-y-1 text-sm">
                    {b.rows.map((r) => (
                      <li key={r.value} className="flex justify-between gap-3">
                        <span className="text-ink">{b.labels[r.value] ?? r.value.replace(/_/g, ' ')}</span>
                        <span className="tabular-nums text-muted-foreground">{r.count.toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">None</p>
                )}
              </div>
            ))}
          </div>
        )}
      </State>
    </CardContent>
  </Card>
)
